import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DatePicker as MuiDatePicker,
  DateRange,
  DateRangePicker,
  MultiInputDateRangeField,
} from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'

import { constructShortcutsItems } from '@/common/utils/date'

import { DateRangeDropdown } from './DateRangeDropdown'
import {
  DatePickerDropdownValue,
  DatePickerErrorProps,
  DatePickerProps,
  DatePickerRange,
  getDatePickerErrors,
} from './types'
import {
  allDropDownOptions,
  firstOfMonth,
  getDateRangeInDays,
  getPreviousMonthDate,
  isDateMatch,
  isLastMonth,
  isThisMonth,
} from './utils'

import './index.scss'

type MuiRange = DateRange<DateTime>

/** Convert the public `{ from, to }` JS-Date range into MUI's `[start, end]` Luxon tuple. */
const toMuiRange = (range: DatePickerRange): MuiRange => [
  range.from ? DateTime.fromJSDate(range.from) : null,
  range.to ? DateTime.fromJSDate(range.to) : null,
]

/** Convert MUI's `[start, end]` Luxon tuple back into the public `{ from, to }` JS-Date range. */
const fromMuiRange = (value: MuiRange): DatePickerRange => ({
  from: value[0] ? value[0].toJSDate() : null,
  to: value[1] ? value[1].toJSDate() : null,
})

/**
 * DatePicker range inputs and calendar, built on MUI X Pro `<DateRangePicker>`.
 *
 * The public API ({@link DatePickerProps}) is unchanged from the previous
 * react-day-picker implementation so all consumers keep working. Internally we drive a
 * controlled `<DateRangePicker>` (value as a Luxon `[start, end]` tuple) and reshape that
 * value per the selected dropdown mode:
 * - `custom` — normal two-calendar range
 * - `singleDay` — one calendar; both endpoints collapse to the picked day
 * - `noStart` — open-ended start (`[null, to]`); start input disabled
 * - `noEnd` — open-ended end (`[from, null]`); end input disabled
 * - `thisMonth` / `lastMonth` — preset ranges committed immediately
 *
 * The calendar shows MUI's in-calendar shortcuts (This Month, Last Month, This/Last Quarter,
 * This Year) and has no Done/Cancel action bar — selections commit immediately. A normal range
 * commits on `onAccept` (the second click); the single-endpoint modes (`singleDay`/`noStart`/
 * `noEnd`) have no two-click "complete range" event, so they commit on `onChange` instead.
 *
 * @param range - date range selected
 * @param rangeLimit - number of days to limit date range selection
 * @param setRange - callback to set date range
 * @param dropdownDefault - optional default dropdown option
 * @param dropdownOptions - options to have available in the dropdown
 * @param requireDates - require a user to have from and to dates (i.e., no null / undefined fields)
 */
export const DatePicker = ({
  disableDropdown,
  dropdownDefault,
  dropdownOptions = allDropDownOptions,
  range,
  rangeLimit,
  requireDates,
  maxDate,
  minDate,
  onOpen,
  onClose,
  setRange,
}: DatePickerProps) => {
  const { t } = useTranslation()

  const [value, setValue] = useState<MuiRange>(() => toMuiRange(range))
  const [dropdownValue, setDropdownValue] = useState<DatePickerDropdownValue>(
    dropdownDefault || dropdownOptions[0]
  )
  const [error, setError] = useState<DatePickerErrorProps>({} as DatePickerErrorProps)
  // Set whenever a selection commits (accept or single-endpoint change) so the close handler
  // can tell a real commit from a click-away and skip the revert in the former case.
  const acceptedRef = useRef(false)

  const singleDay = dropdownValue === 'singleDay'
  const noStart = dropdownValue === 'noStart'
  const noEnd = dropdownValue === 'noEnd'
  // singleDay renders its own single `<DatePicker>` below; this only governs the
  // open-ended `<DateRangePicker>` modes (one calendar, commit-on-change, accept-skip).
  const showOneCalendar = noStart || noEnd

  // The calendar always uses MUI's in-calendar shortcuts (This Month, Last Month, This/Last
  // Quarter, This Year) with no Done/Cancel action bar — selections commit immediately, matching
  // the Elevate filter date picker. `disableDropdown` only controls the preset DateRangeDropdown
  // above the inputs and the compact-vs-fill input layout.
  const shortcutsItems = useMemo(
    () => constructShortcutsItems(maxDate ? DateTime.fromJSDate(maxDate) : undefined),
    [maxDate]
  )

  // Key the sync effects off the actual date values, not the `range` object identity:
  // consumers (e.g. TripCountsNav) pass `range` as an inline `{ from, to }` literal, so a new
  // reference arrives on every parent render. Depending on `range` would replay both effects —
  // wiping draft edits/errors — even when the dates are unchanged.
  const fromMs = range.from?.getTime()
  const toMs = range.to?.getTime()

  // Sync the working value when the controlled `range` prop changes.
  useEffect(() => setValue(toMuiRange(range)), [fromMs, toMs])

  /**
   * Keep the dropdown selection in sync with the committed range (e.g., a custom range that
   * happens to be exactly this/last month flips the dropdown to that preset).
   */
  const syncDropdownValue = () => {
    const startDate = range.from
    const endDate = range.to
    const thisMonthValid = endDate && startDate && isThisMonth(startDate, endDate)
    const lastMonthValid = endDate && startDate && isLastMonth(startDate, endDate)
    const isSameDate = startDate && isDateMatch(startDate, endDate)

    switch (dropdownValue) {
      case 'thisMonth':
        if (!thisMonthValid) setDropdownValue(lastMonthValid ? 'lastMonth' : 'custom')
        break
      case 'lastMonth':
        if (!lastMonthValid) setDropdownValue(thisMonthValid ? 'thisMonth' : 'custom')
        break
      case 'custom':
        if (thisMonthValid) setDropdownValue('thisMonth')
        else if (lastMonthValid) setDropdownValue('lastMonth')
        break
      case 'noStart':
        startDate && setDropdownValue('custom')
        break
      case 'singleDay':
        if ((startDate || endDate) && !isSameDate) setDropdownValue('custom')
        break
      case 'noEnd':
        endDate && setDropdownValue('custom')
    }
  }

  useEffect(syncDropdownValue, [fromMs, toMs])

  /**
   * Validates the candidate range, populating field errors. MUI prevents out-of-range and
   * out-of-order selection in the calendar; we re-check here to gate the commit and to
   * surface the inline error messages the old picker showed.
   */
  const validateDates = (val: MuiRange): boolean => {
    const from = val[0]?.toJSDate()
    const to = val[1]?.toJSDate()

    if (requireDates && (!from || !to)) {
      setError({
        from: from ? undefined : getDatePickerErrors().required,
        to: to ? undefined : getDatePickerErrors().required,
      })
      return false
    }

    if (from && to) {
      if (from > to) {
        setError({ to: getDatePickerErrors().invalid })
        return false
      }
      if (rangeLimit && getDateRangeInDays(from, to) > rangeLimit) {
        setError({
          to: t('datePicker.maximumRangeError', 'Maximum date range is {{rangeLimit}} days', {
            rangeLimit,
          }),
        })
        return false
      }
    }
    if (minDate) {
      if (from && minDate > from) {
        setError({ from: t('datePicker.minDateError', 'Date is before the earliest allowed') })
        return false
      }
      if (to && minDate > to) {
        setError({ to: t('datePicker.minDateError', 'Date is before the earliest allowed') })
        return false
      }
    }
    if (maxDate) {
      if (from && maxDate < from) {
        setError({ from: t('datePicker.maxDateError', 'Date is after the latest allowed') })
        return false
      }
      if (to && maxDate < to) {
        setError({ to: t('datePicker.maxDateError', 'Date is after the latest allowed') })
        return false
      }
    }

    setError({} as DatePickerErrorProps)
    return true
  }

  /** Reshape the raw calendar selection to match the active dropdown mode. */
  const handleChange = (newValue: MuiRange) => {
    let next = newValue
    if (noStart) {
      next = [null, newValue[1] ?? newValue[0] ?? null]
    } else if (noEnd) {
      next = [newValue[0] ?? newValue[1] ?? null, null]
    }
    setValue(next)
    if (error.from || error.to) setError({} as DatePickerErrorProps)
    // The single-endpoint modes never fire a two-click `onAccept`, so commit them here.
    if (showOneCalendar && validateDates(next)) {
      acceptedRef.current = true
      setRange(fromMuiRange(next))
    }
  }

  /** Commit on accept (a shortcut or a completed two-click range) when valid. */
  const handleAccept = (val: MuiRange) => {
    // Single-endpoint modes are driven entirely by handleChange, which reshapes the raw
    // selection to the mode and commits. A shortcut also fires onAccept with the *raw*
    // (unreshaped) range, so re-committing here would clobber that reshaped commit — skip it.
    if (showOneCalendar) return
    if (validateDates(val)) {
      acceptedRef.current = true
      setRange(fromMuiRange(val))
    }
  }

  // Presets bypass the calendar, so gate them through the same validation as the Done flow
  // (rangeLimit / minDate / maxDate); on failure the field shows the error instead of committing.
  const commitPresetRange = (presetRange: DatePickerRange) => {
    const presetValue = toMuiRange(presetRange)
    setValue(presetValue)
    if (validateDates(presetValue)) {
      // Flag the commit so the close-time revert (onClose) treats picking a preset while the
      // picker is open as a real commit, not a click-away that discards it.
      acceptedRef.current = true
      setRange(presetRange)
    }
  }

  const handleDropdownChange = (next: DatePickerDropdownValue) => {
    setDropdownValue(next)
    setError({} as DatePickerErrorProps)
    const today = DateTime.now().startOf('day').toJSDate()
    switch (next) {
      case 'thisMonth': {
        commitPresetRange({ from: firstOfMonth(today), to: today })
        break
      }
      case 'lastMonth': {
        const lastMonth = getPreviousMonthDate(today)
        commitPresetRange({ from: firstOfMonth(lastMonth), to: lastMonth })
        break
      }
      // Fall back to the other endpoint so a populated date survives the mode switch
      // (e.g. noStart's [null, date] -> singleDay/noEnd keeps the date instead of dropping it).
      case 'singleDay': {
        const day = value[0] ?? value[1] ?? null
        setValue([day, day])
        break
      }
      case 'noStart':
        setValue([null, value[1] ?? value[0] ?? null])
        break
      case 'noEnd':
        setValue([value[0] ?? value[1] ?? null, null])
        break
      // 'custom': keep the current value as-is
    }
  }

  // rangeLimit is enforced proactively: once a start is chosen, days beyond the limit are
  // disabled so an over-long range cannot be selected in the first place.
  const shouldDisableDate = (day: DateTime, position: 'start' | 'end'): boolean => {
    if (!rangeLimit || position !== 'end' || !value[0]) return false
    // Integer calendar-day delta: a raw Luxon diff is fractional (time-of-day, DST) and would
    // flip the disabled/enabled boundary by a day.
    const deltaDays = Math.round(day.startOf('day').diff(value[0].startOf('day'), 'days').days)
    return Math.abs(deltaDays) > rangeLimit
  }

  return (
    <div
      className={`date-picker${disableDropdown ? '' : ' fill-width'}`}
      data-testid="date-picker-component"
    >
      {!disableDropdown && (
        <DateRangeDropdown
          value={dropdownValue}
          dropdownOptions={dropdownOptions}
          onChange={selected => handleDropdownChange(selected as DatePickerDropdownValue)}
        />
      )}
      <div className="inputs">
        {singleDay ? (
          // Single Day is a true single-date picker (one input, one click selects + closes) —
          // not a range picker constrained after the fact. Commits `{ from: day, to: day }`.
          <MuiDatePicker
            value={value[0] ?? value[1] ?? null}
            minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
            maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
            onChange={day => {
              setValue([day, day])
              if (error.from || error.to) setError({} as DatePickerErrorProps)
            }}
            onAccept={day => {
              const next: MuiRange = [day, day]
              setValue(next)
              if (validateDates(next)) {
                acceptedRef.current = true
                setRange(fromMuiRange(next))
              }
            }}
            onOpen={() => {
              acceptedRef.current = false
              onOpen?.()
            }}
            onClose={() => {
              onClose?.()
              // Dismissed without a commit: discard the visible draft back to the committed
              // `range` so the input never shows an unapplied date, and re-sync the dropdown
              // so an uncommitted mode switch reverts alongside the discarded draft.
              if (!acceptedRef.current) {
                setValue(toMuiRange(range))
                syncDropdownValue()
                setError({} as DatePickerErrorProps)
              }
              acceptedRef.current = false
            }}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: !!error.from,
                helperText: error.from,
                inputProps: { 'aria-label': t('datePicker.singleDate', 'Date') },
                sx: { width: '100%', minWidth: 0 },
              },
            }}
          />
        ) : (
          <DateRangePicker
            value={value}
            calendars={showOneCalendar ? 1 : 2}
            minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
            maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
            shouldDisableDate={shouldDisableDate}
            onChange={handleChange}
            onAccept={handleAccept}
            slots={{ field: MultiInputDateRangeField }}
            slotProps={{
              // In-calendar shortcuts in place of a Done/Cancel action bar (all range variants).
              shortcuts: { items: shortcutsItems },
              textField: ownerState => {
                // MultiInputDateRangeField passes `position` at runtime; it isn't on the
                // shared FieldOwnerState type, so read it through a narrow cast.
                const { position } = ownerState as { position?: 'start' | 'end' }
                const isStart = position === 'start'
                return {
                  size: 'small',
                  // Drop the redundant floating "Start"/"End" labels (the consumer's section
                  // label and the `–` separator already convey this); keep them for a11y.
                  label: '',
                  inputProps: {
                    'aria-label': isStart
                      ? t('datePicker.startDate', 'Start date')
                      : t('datePicker.endDate', 'End date'),
                  },
                  // disableDropdown (horizontal nav, e.g. trip-counts): fixed compact width.
                  // Dropdown variant (narrow sidebars): the field Root is a flex row of two
                  // `fullWidth` inputs — `flex:1` makes them split the Root evenly and `minWidth:0`
                  // lets each shrink below its content width so the pair fits (no End clipping).
                  sx: disableDropdown ? { width: 140 } : { flex: 1, minWidth: 0 },
                  disabled: (noStart && isStart) || (noEnd && !isStart),
                  error: isStart ? !!error.from : !!error.to,
                  helperText: isStart ? error.from : error.to,
                }
              },
            }}
            // Uncontrolled open; selections commit immediately (no Done/Cancel). Forward the
            // consumer's open/close callbacks.
            onOpen={() => {
              // Start each calendar session with a clean slate: a dropdown preset can set
              // acceptedRef while the calendar is closed, and without this reset that stale
              // flag would make the next click-away skip the revert of an uncommitted draft.
              acceptedRef.current = false
              onOpen?.()
            }}
            onClose={() => {
              onClose?.()
              // Click-away without a commit (e.g. a half-picked custom range): discard the
              // partial selection back to the committed `range` so the inputs never show an
              // unapplied range. `range` here is the committed value since nothing committed
              // this interaction; the accepted path skips this to avoid clobbering the commit.
              if (!acceptedRef.current) {
                setValue(toMuiRange(range))
                syncDropdownValue()
                setError({} as DatePickerErrorProps)
              }
              acceptedRef.current = false
            }}
          />
        )}
      </div>
    </div>
  )
}
